import { useState } from "preact/hooks";
import type { FlowState, SimReady } from "../flow/machine";
import type { InstallRunner } from "../flow/runner";
import { t } from "../i18n";

/**
 * Pre-flight checklist item "SIM ready" (D18). QuickHub OS has no eSIM manager, so an eSIM
 * profile must be activated (and tested) on stock Android before the phone is flashed; it then
 * persists in the eUICC but cannot be changed until the phone is restored to stock. The customer
 * picks physical SIM or activated eSIM and ticks the confirmation; the runner records it and the
 * flow machine keeps the unlock step unreachable until it has.
 */
export function SimChecklist({ runner, s }: { runner: InstallRunner; s: FlowState }) {
  const [kind, setKind] = useState<SimReady | undefined>(s.simReady);
  const [ticked, setTicked] = useState(false);
  const confirmed = s.simReady;
  const canConfirm = kind !== undefined && ticked && kind !== confirmed;
  return (
    <section class={confirmed ? "notice ok" : "notice warn"} aria-labelledby="sim-h">
      <h3 id="sim-h">{t("sim.heading")}</h3>
      <p>{t("sim.intro")}</p>
      <fieldset class="stack" style="border:0;padding:0;margin:0">
        <legend class="sr-only">{t("sim.heading")}</legend>
        <label style="font-weight:400">
          <input type="radio" name="sim-kind" value="physical" checked={kind === "physical"} onChange={() => setKind("physical")} /> {t("sim.option.physical")}
        </label>
        <label style="font-weight:400">
          <input type="radio" name="sim-kind" value="esim_activated" checked={kind === "esim_activated"} onChange={() => setKind("esim_activated")} /> {t("sim.option.esim_activated")}
        </label>
      </fieldset>
      <p class="hint">{t("sim.consequence")}</p>
      <label style="font-weight:400">
        <input type="checkbox" checked={ticked} onChange={(e) => setTicked((e.target as HTMLInputElement).checked)} /> {t("sim.confirm_label")}
      </label>
      <div class="row">
        <button type="button" class="primary" disabled={!canConfirm} onClick={() => kind && runner.confirmSim(kind)}>{t("sim.button")}</button>
        {confirmed && <span role="status">{t(`sim.confirmed.${confirmed}`)}</span>}
      </div>
    </section>
  );
}

/** The one-line eSIM reminder shown on the recovery, remove and stock-restored screens. */
export function EsimNote({ where }: { where: "recovery" | "remove" | "stock" }) {
  return <p class="hint">{t(`sim.esim_note.${where}`)}</p>;
}
